import { renderToStaticMarkup } from 'react-dom/server';
import parse from 'html-react-parser';

import { toJS } from 'mobx';

import {
  ContentType, LessonMode, PartialScoringType, QuestionFeedbackState, questionFeedbackFromScores
} from '../../Constants';

import lessonManager from '../managers/LessonManager';
import userManager from '../managers/UserManager';

import questionFeedbackManager from '../managers/QuestionFeedbackManager';
import questionService from '../services/QuestionService';
import UtilsService from '../services/UtilsService';

import { Behavior } from './Behavior';

export class CategorizeBehavior extends Behavior {
  checkForValidResponse = (response) => {
    // Valid if any of the prompts has an answer
    return Boolean(response.prompts && Object.values(response.prompts).some((answers) => answers.length > 0));
  }

  isAutoScored = (model) => {
    return ContentType.getAutoscore(model.type) && !model.survey && !model.unscored;
  }

  setCorrectAnswer = (state, model) => {
    if (!model?.validation?.prompts) {
      return;
    }

    const modelValidationPrompts = toJS(model.validation.prompts);

    for (const cell of Object.values(modelValidationPrompts)) {
      if (!Array.isArray(cell)) continue;
      for (const answer of cell) {
        if (answer.imageUrl && answer.imageUrl.includes('authKey')) {
          answer.imageUrl = UtilsService.swapAuthKey(answer.imageUrl, userManager.authKey);
        }
        if (answer.text) {
          answer.text = renderToStaticMarkup(parse(answer.text, {
            replace: (domNode) => {
              if (domNode.name === 'img' && domNode.attribs?.src && domNode.attribs.src.includes('authKey')) {
                domNode.attribs.src = UtilsService.swapAuthKey(domNode.attribs.src, userManager.authKey);
              }
            }
          }));
        }
      }
    }

    // Show correct answer: only place prompts explicitly marked correct in cells (exclude distractors).
    const cellPromptsCorrectOnly = {};
    for (const [cellId, cell] of Object.entries(modelValidationPrompts)) {
      if (!Array.isArray(cell)) {
        cellPromptsCorrectOnly[cellId] = [];
        continue;
      }
      // Backward compatibility: if isCorrect is missing, treat it as true.
      // Only explicit `isCorrect: false` is treated as incorrect.
      cellPromptsCorrectOnly[cellId] = cell.filter((p) => p && p.isCorrect !== false);
    }

    const correctIds = new Set();
    for (const arr of Object.values(cellPromptsCorrectOnly)) {
      if (!Array.isArray(arr)) continue;
      for (const p of arr) {
        if (p?.id != null) correctIds.add(p.id);
      }
    }

    // Incorrect / unused choices stay in answer bank(s) like initial state (not dragged).
    const initialLayout = this.getInitializedResponse(model.lessonElementId, model);
    const bankPromptsFiltered = {};
    for (const [bankKey, arr] of Object.entries(initialLayout.bankPrompts || {})) {
      bankPromptsFiltered[bankKey] = (Array.isArray(arr) ? arr : []).filter((p) => p && !correctIds.has(p.id));
    }

    const temp = {
      prompts: {},
      cellPrompts: UtilsService.safeMobxClone(cellPromptsCorrectOnly),
      bankPrompts: UtilsService.safeMobxClone(bankPromptsFiltered),
      isScored: false,
      isScoredCorrect: false,
      lessonElementId: model.lessonElementId
    };
    this._setResponsePrompts(temp);
    state.setCurrentResponse(UtilsService.safeMobxClone(temp));
  }

  resetStudentAnswer = (state, validation) => {
    state.setCurrentResponse(UtilsService.safeMobxClone(state.cachedResponseAnswer));
  }

  /**
   * Validation-only correctness (used when answerResults is empty / legacy scoring).
   * - If validation rows use `isCorrect`, respect true/false for the matching row.
   * - If `isCorrect` is missing (legacy authoring), any id/text match in this cell mirrors old behavior: treat as correct.
   */
  _isAnswerCorrect = (id, model, responseAnswer, cellKey) => {
    const currentPrompts = responseAnswer.cellPrompts;
    const validationByCell = model?.validation?.prompts;
    if (!currentPrompts || !validationByCell) {
      return false;
    }

    let currentCellId = null;
    let currentPrompt = null;
    if (cellKey) {
      const promptsInCell = currentPrompts[cellKey];
      if (!Array.isArray(promptsInCell)) return false;

      currentPrompt = promptsInCell.find((p) => p && (p.id === id || String(p.id) === String(id))) || null;
      if (!currentPrompt) return false;

      currentCellId = cellKey;
    } else {
      for (const [cellId, prompts] of Object.entries(currentPrompts)) {
        const found = Array.isArray(prompts)
          ? prompts.find((p) => p && (p.id === id || String(p.id) === String(id)))
          : undefined;
        if (found) {
          currentCellId = cellId;
          currentPrompt = found;
          break;
        }
      }
      if (!currentCellId || !currentPrompt) return false;
    }

    const cellValidation = validationByCell[currentCellId];
    if (!Array.isArray(cellValidation)) {
      return false;
    }

    const { cleanStringForScoring } = UtilsService;
    const currentText = currentPrompt.text;

    for (const vp of cellValidation) {
      const idMatch = vp.id != null && (vp.id === id || String(vp.id) === String(id));
      const textMatch = vp.text != null && currentText != null &&
        cleanStringForScoring(vp.text) === cleanStringForScoring(currentText);
      if (idMatch || textMatch) {
        if (vp.isCorrect === false) return false;
        if (vp.isCorrect === true) return true;
        return true;
      }
    }
    return false;
  }

  _setResponsePrompts = (responseAnswer) => {
    const promptsObj = {};
    for (const [cellId, promptsArray] of Object.entries(responseAnswer.cellPrompts)) {
      // Submit stable ids so translated labels are not scored against original-language text.
      // Legacy items without an id still send the visible text.
      promptsObj[cellId] = promptsArray.map((prompt) => prompt.id || prompt.text);
    }
    responseAnswer.prompts = promptsObj;
  }

  _getPromptById = (model, answerId) => {
    const prompts = model.prompts;
    if (!prompts) return undefined;
    if (Array.isArray(prompts)) {
      return prompts.find((p) => p.id === answerId);
    }
    for (const arr of Object.values(prompts)) {
      if (!Array.isArray(arr)) continue;
      const found = arr.find((p) => p.id === answerId);
      if (found) return found;
    }
    return undefined;
  }

  /**
   * Get manual layout (left, top, width, height) for a prompt id from model.responseItems.
   * responseItems may be an array or a map (cellId -> array of items).
   * When multi answer bank + manual layout, missing top/left on responseItem default to 0.
   */
  _getLayoutForPromptId = (model, promptId) => {
    const responseItems = model.responseItems;
    if (!responseItems) return {};
    const layoutFields = ['left', 'top', 'width', 'height'];
    const isMultiManual = Boolean(model.useSeparateBanksPerCategory && model.answerLayoutType === 'manual' && (model.columnLabels?.length || 0) >= 2);

    let layout = {};
    if (Array.isArray(responseItems)) {
      const item = responseItems.find((r) => r && r.id === promptId);
      if (!item) return {};
      layout = layoutFields.reduce((acc, key) => {
        if (item[key] !== undefined && item[key] !== null) acc[key] = item[key];
        return acc;
      }, {});
    } else if (typeof responseItems === 'object') {
      for (const arr of Object.values(responseItems)) {
        if (!Array.isArray(arr)) continue;
        const item = arr.find((r) => r && r.id === promptId);
        if (item) {
          layout = layoutFields.reduce((acc, key) => {
            if (item[key] !== undefined && item[key] !== null) acc[key] = item[key];
            return acc;
          }, {});
          break;
        }
      }
    }

    if (isMultiManual) {
      if (layout.left === undefined || layout.left === null) layout.left = 0;
      if (layout.top === undefined || layout.top === null) layout.top = 0;
    }
    return layout;
  }

  /** Return a prompt object with manual layout merged from model.responseItems so layout is conserved when saving. */
  _mergePromptWithLayout = (model, prompt) => {
    if (!prompt) return prompt;
    const layout = this._getLayoutForPromptId(model, prompt.id);
    if (Object.keys(layout).length === 0) return { ...prompt };
    return { ...prompt, ...layout };
  }

  setResponseData = ({ sourceZoneKey, destinationZoneKey, answerId }, responseAnswer, model) => {
    const { answersPersist } = model;
    const answer = this._getPromptById(model, answerId);

    const getCellArray = (zoneKey) => {
      // If cellId has bank in it, then the cellId is zoneKey. Otherwise it is cell+zoneKey
      if (zoneKey.includes('bank')) {
        return responseAnswer.bankPrompts[zoneKey];
      } else {
        return responseAnswer.cellPrompts[`cell${zoneKey}`];
      }
    };

    const sourceCell = getCellArray(sourceZoneKey);
    const destinationCell = getCellArray(destinationZoneKey);

    // Removing so we can move items back to answer bank
    // if (destinationZoneKey.includes('bank')) {
    //   // You cannot move things back to the bank
    //   return;
    // }

    // Remove answer from source cell if the cell exists
    if (sourceCell) {
      // If answersPersist is true, then we do not remove it if this is a bank
      if (!(answersPersist && sourceZoneKey.includes('bank'))) {
        for (let i = 0; i < sourceCell.length; i++) {
          if (sourceCell[i].id === answerId) {
            sourceCell.splice(i, 1);
            break;
          }
        }
      }
    }

    // Add answer to destination cell if the cell exists (merge layout from responseItems so manual layout is conserved on save)
    if (destinationCell) {
      // If answersPersist is true and this answerId is already in the cell, we do not add it again
      if (!(answersPersist && destinationCell.find((prompt) => prompt.id === answerId))) {
        destinationCell.push(this._mergePromptWithLayout(model, answer));
      }
    }

    this._setResponsePrompts(responseAnswer); // Update the response data
    lessonManager.stampResponsePublisherTranslationId(responseAnswer);
  }

  getAnswerFeedback = (id, responseAnswer) => {
    return true;
  }

  getInitializedResponse = (lessonElementId, model) => {
    /*
    Decisions here need to be justified
    Usually we store data about question state in prompts. However, in this case, we would need to do a lot more work if we only stored the answer state.
    Therefore, along with `prompts`, which stores answer ids per cell, we also store cellPrompts and bankPrompts which divide the prompts into their visual locations.
    This also means that we need to convert the data stored in the cellPrompts and bankPrompts objects into the final answer format.
    This is done by calling _setResponsePrompts with the full answerResponse object.
    */
    const showDuplicateAnswers = model.duplicateAnswers;
    const seenAnswers = new Set();

    const numCells = model.rowLabels.length * model.columnLabels.length;
    const columnCount = model.columnLabels.length;
    const promptsObject = {};
    for (let i = 0; i < numCells; i++) {
      promptsObject[`cell${i}`] = [];
    }

    const { prompts } = model;
    const isPromptMap = prompts && !Array.isArray(prompts) && typeof prompts === 'object';
    const useSeparateBanksPerCategory = Boolean(model.useSeparateBanksPerCategory && columnCount >= 2);
    const isManualLayout = model.answerLayoutType === 'manual';

    let bankObject = {};

    if (isPromptMap) {
      // model.prompts is a map: cellId (e.g. "cell0") -> array of prompt objects
      if (useSeparateBanksPerCategory) {
        // One bank per category (column): bank0 = column 0 cells, bank1 = column 1, ...
        for (let c = 0; c < columnCount; c++) {
          bankObject[`bank${c}`] = [];
        }
        for (const [cellId, arr] of Object.entries(prompts)) {
          if (!Array.isArray(arr)) continue;
          const match = cellId.match(/^cell(\d+)$/);
          const cellIndex = match ? parseInt(match[1], 10) : NaN;
          if (Number.isNaN(cellIndex)) continue;
          const col = cellIndex % columnCount;
          const bankKey = `bank${col}`;
          for (const prompt of arr) {
            const promptAnswer = prompt.text;
            if (!showDuplicateAnswers && seenAnswers.has(promptAnswer)) continue;
            seenAnswers.add(promptAnswer);
            bankObject[bankKey].push(this._mergePromptWithLayout(model, prompt));
          }
        }
      } else {
        // Single bank: dump all prompts from all cells into bank0 (or bank0..bankN for manual)
        const flatPrompts = [];
        for (const arr of Object.values(prompts)) {
          if (Array.isArray(arr)) flatPrompts.push(...arr);
        }
        for (let i = 0; i < flatPrompts.length; i++) {
          const prompt = flatPrompts[i];
          const bankKey = isManualLayout ? `bank${i}` : 'bank0';
          if (!bankObject[bankKey]) bankObject[bankKey] = [];
          const promptAnswer = prompt.text;
          if (!showDuplicateAnswers && seenAnswers.has(promptAnswer)) continue;
          seenAnswers.add(promptAnswer);
          bankObject[bankKey].push(this._mergePromptWithLayout(model, prompt));
        }
      }
    } else {
      // model.prompts is a flat array (legacy)
      const promptsArray = Array.isArray(prompts) ? prompts : [];
      for (let i = 0; i < promptsArray.length; i++) {
        const prompt = promptsArray[i];
        const bankKey = isManualLayout ? `bank${i}` : 'bank0';
        if (!bankObject[bankKey]) {
          bankObject[bankKey] = [];
        }
        const promptAnswer = prompt.text;
        if (!showDuplicateAnswers && seenAnswers.has(promptAnswer)) {
          // eslint-disable-next-line no-continue
          continue;
        }
        seenAnswers.add(promptAnswer);
        bankObject[bankKey].push(this._mergePromptWithLayout(model, prompt));
      }
    }

    const responseAnswer = {
      ...questionService.generateInitializedQuestionResponse({
        prompts: {}, // Computed from cellPrompts each time they change
        cellPrompts: promptsObject,
        bankPrompts: bankObject,
        isScored: false,
        isScoredCorrect: false,
        lessonElementId
      })
    };
    return responseAnswer;
  }

  getCorrectAnswersText = (model) => {
    return '';
  }

  getQuestionFeedbackText = (model, correctFeedback, incorrectFeedback, correctFeedbackBody, incorrectFeedbackBody, mode, questionIsCorrect, currentAttemptCount, { lessonElementState }) => {
    let textBody = '';
    if (mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.STUDENT_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) {
      textBody += UtilsService.previewWarning();
    } else {
      if (typeof currentAttemptCount !== 'undefined' && currentAttemptCount !== -1 && currentAttemptCount > 1) {
        textBody += UtilsService.saveWarning();
      } else {
        textBody += UtilsService.saveCommit();
      }
    }

    let textHeader = '';
    let attemptsText = '';
    
    // Add attempts remaining text for practice mode when answer is incorrect or partially correct
    if (questionFeedbackManager.isPractice && 
        (questionIsCorrect === QuestionFeedbackState.INCORRECT || questionIsCorrect === QuestionFeedbackState.PARTIAL_CORRECT) &&
        model.lessonElementId) {
      attemptsText = questionFeedbackManager.getAttemptsRemainingText(model.lessonElementId);
    }

    if (questionIsCorrect === QuestionFeedbackState.CORRECT) {
      textHeader = correctFeedback;
      if (correctFeedbackBody) {
        textBody = `${correctFeedbackBody} ${textBody}`;
      }
    } else if (questionIsCorrect === QuestionFeedbackState.PARTIAL_CORRECT) {
      textHeader = `${UtilsService.partiallyCorrect()}`;
      let custom = '';
      if (incorrectFeedbackBody) {
        custom = `${incorrectFeedbackBody }<br/>`;

        textBody = `${UtilsService.partiallyCorrectSelectPrompt2()}.<br/>${ custom }${textBody}`;
      } else {
        textBody = `${UtilsService.partiallyCorrectSelectPrompt2()}. ${ textBody}`;
      }
    } else {
      textHeader = incorrectFeedback;
      if (incorrectFeedbackBody) {
        textBody = `${incorrectFeedbackBody} ${textBody}`;
      }
    }

    // Add attempts text after the header if it exists
    if (attemptsText) {
      textHeader += attemptsText;
    }

    let finalText = '<div class=\'question-feedback-header\'>';
    finalText += textHeader;
    finalText += '</div>';
    finalText += '<div class=\'question-feedback-body\'>';
    finalText += textBody;
    finalText += '</div>';
    return finalText;
  }

  _normalizeCategorizeCellId = (value) => {
    if (value == null || value === '') return null;
    const s = String(value);
    if (s.startsWith('cell')) return s;
    if (/^\d+$/.test(s)) return `cell${s}`;
    return s;
  }

  _getPromptTextInCell = (responseAnswer, answerId, cellKey) => {
    const promptsInCell = responseAnswer?.cellPrompts?.[cellKey];
    if (!Array.isArray(promptsInCell)) return null;
    const prompt = promptsInCell.find(
      (p) => p && (p.id === answerId || String(p.id) === String(answerId))
    );
    return prompt?.text != null ? String(prompt.text) : null;
  }

  _getAnswerResultRowText = (r) => {
    if (!r) return '';
    const t = r.text ?? r.prompt ?? r.answer ?? r.answerText ?? '';
    return String(t);
  }

  /** answerResults rows use `cell` for the cell key (e.g. 'cell0'). */
  _getAnswerResultRowCell = (r) => (r && r.cell != null ? r.cell : null)

  /**
   * Match answerResults by normalized cell + prompt text (not answer id).
   */
  _findStrictCategorizeAnswerResultByText = (answerResults, promptText, cellKey) => {
    if (!answerResults?.length || promptText == null) return undefined;
    const wantCell = this._normalizeCategorizeCellId(cellKey);
    if (wantCell == null) return undefined;

    const { cleanStringForScoring } = UtilsService;
    const normPrompt = cleanStringForScoring(String(promptText));

    return answerResults.find((r) => {
      const rCell = this._normalizeCategorizeCellId(this._getAnswerResultRowCell(r));
      if (rCell == null || rCell !== wantCell) return false;
      return cleanStringForScoring(this._getAnswerResultRowText(r)) === normPrompt;
    });
  }

  isAnswerCorrect = (answerId, lessonElementState, { cellKey = null } = {}) => {
    const lessonElementId = lessonElementState?.currentResponseAnswer?.lessonElementId;
    if (!lessonElementId) {
      return false;
    }
    const uiState = questionFeedbackManager.getUiState(lessonElementId);
    if (uiState.showAnswers) {
      return true;
    }

    const responseAnswer = lessonElementState.currentResponseAnswer;
    const answerResults = responseAnswer?.answerResults;
    if (answerResults && answerResults.length > 0 && cellKey != null) {
      const wantCell = this._normalizeCategorizeCellId(cellKey);
      const byId = answerResults.find((r) => {
        const rCell = this._normalizeCategorizeCellId(this._getAnswerResultRowCell(r));
        if (rCell == null || rCell !== wantCell) return false;
        return r.answerId === answerId || String(r.answerId) === String(answerId);
      });
      if (byId) {
        return byId.match === true;
      }
      const promptText = this._getPromptTextInCell(responseAnswer, answerId, cellKey);
      if (promptText == null) {
        return false;
      }
      const result = this._findStrictCategorizeAnswerResultByText(answerResults, promptText, cellKey);
      if (!result) {
        return false;
      }
      return result.match === true;
    }

    const model = lessonManager.getSlideModel(lessonElementId);
    const isCorrect = this._isAnswerCorrect(answerId, model, responseAnswer, cellKey);
    return isCorrect;
  }

  isQuestionCorrect = (lessonElementState, lessonElementId) => {
    if (!lessonElementState) {
      return QuestionFeedbackState.INCORRECT;
    }

    const uiState = questionFeedbackManager.getUiState(lessonElementId);

    if (uiState.showAnswers) {
      return QuestionFeedbackState.CORRECT;
    }
    return questionFeedbackFromScores(lessonElementState.submittedItemScore, lessonElementState.maxScore);
  }
}

export default new CategorizeBehavior();
